import { useRef, useEffect } from 'react';
import { useCodeBlockEditorContext } from '@mdxeditor/editor';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { CodeBlockWrapper, CodeBlockLanguageTag, CodeTextarea } from './styled';

// Editable plain-text code block. Auto-grows its height to fit content.
export const DefaultCodeBlockEditor = ({ code, language }) => {
    const { setCode } = useCodeBlockEditorContext();
    const textareaRef = useRef(null);

    useEffect(() => {
        const el = textareaRef.current;
        if (el) {
            // Imperative height reset so the textarea shrinks as well as grows.
            el.style.height = 'auto';
            el.style.height = el.scrollHeight + 'px';
        }
    }, [code]);

    return (
        <CodeTextarea
            ref={textareaRef}
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder={language ? `${language} code...` : 'Code...'}
        />
    );
};

// Read-only code block with syntax highlighting via react-syntax-highlighter.
export const ReadOnlyCodeBlock = ({ code, language }) => {
    useCodeBlockEditorContext(); // required by MDXEditor for all block descriptors

    return (
        <CodeBlockWrapper>
            {language && (
                <CodeBlockLanguageTag>{language}</CodeBlockLanguageTag>
            )}
            <SyntaxHighlighter
                language={language || 'text'}
                style={vscDarkPlus}
                // customStyle and codeTagProps are library API props that override
                // react-syntax-highlighter's own inline defaults on <pre>/<code>.
                // They cannot be replaced with CSS selectors because the library
                // injects its own inline styles that would take precedence.
                customStyle={{
                    margin: 0,
                    padding: '12px 16px',
                    background: 'transparent',
                    fontSize: '13px',
                    lineHeight: '1.6'
                }}
                codeTagProps={{
                    style: {
                        fontFamily:
                            '"Fira Code", "Fira Mono", "Roboto Mono", monospace'
                    }
                }}
            >
                {code}
            </SyntaxHighlighter>
        </CodeBlockWrapper>
    );
};

export const defaultCodeBlockDescriptor = {
    priority: 0,
    match: () => true,
    Editor: DefaultCodeBlockEditor
};

export const readOnlyCodeBlockDescriptor = {
    priority: 0,
    match: () => true,
    Editor: ReadOnlyCodeBlock
};
